import React from 'react'
import {
  Button,
  Form,
  Input,
  Select,
  message,
  Alert,
  Result,
  Modal,
  Spin
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ReloadOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import { productApi } from 'api/product'
import { seoPageApi } from 'api/seoPages'
import { ISubCategory } from 'models/product'
import {
  ISeoPageFormData,
  ICreateSeoPageRequest,
  IUpdateSeoPageRequest,
  ISelectedFilter,
  ISeoPage,
  ESeoPageType
} from 'models/seoPages'
import { showServerError } from 'shared/utils/showServerError'
import { SeoFields, MainTitle } from 'components/ui'
import { superAdminRoutes } from 'shared/constants/routes'
import { FilterSelector } from '../FilterSelector/FilterSelector'
import {
  validateSelectorText,
  validateUrl,
  convertToUrlFriendly
} from './utils/seoValidation'
import styles from './SeoPageForm.module.scss'

const SEO_PAGES_LIST_PATH = `${superAdminRoutes.ACTIONS}/${superAdminRoutes.ACTIONS_SETTINGS}`

export const SeoPageForm: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [form] = Form.useForm()

  const [subCategories, setSubCategories] = React.useState<ISubCategory[]>([])
  const [currentPage, setCurrentPage] = React.useState<ISeoPage | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [selectedSubCategoryId, setSelectedSubCategoryId] =
    React.useState<string>()
  const [selectedFilters, setSelectedFilters] = React.useState<
    ISelectedFilter[]
  >([])
  const [loadingError, setLoadingError] = React.useState<string | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)
  const [categoryPath, setCategoryPath] =
    React.useState<string>('horsesmart.store/')
  const [isUrlManuallyEdited, setIsUrlManuallyEdited] = React.useState(false)

  const isEditMode = Boolean(id)

  React.useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        setLoadingError(null)

        if (isEditMode && id) {
          const pageResponse = await seoPageApi.getSeoPageById(id)
          const pageData = pageResponse.data
          setCurrentPage(pageData)
          setSelectedSubCategoryId(pageData.subCategory_id)

          const selectedFilters: ISelectedFilter[] = pageData.filters.map(
            (filter) => ({
              name: filter.name,
              selectedValues: filter.values
            })
          )
          setSelectedFilters(selectedFilters)

          const fullUrl = pageData.url.replace('horsesmart.store/', '')

          const categoryMatch = fullUrl.match(/^category\/[^/]+\//)

          if (categoryMatch) {
            const categoryPathPart = categoryMatch[0]
            const filterName = fullUrl.replace(categoryPathPart, '')
            setCategoryPath(`horsesmart.store/${categoryPathPart}`)

            form.setFieldsValue({
              name: pageData.name,
              subCategoryId: pageData.subCategory_id,
              url: filterName,
              seoInfo: pageData.seoInfo
            })
          } else {
            form.setFieldsValue({
              name: pageData.name,
              subCategoryId: pageData.subCategory_id,
              url: fullUrl,
              seoInfo: pageData.seoInfo
            })
          }

          if (pageData.type === ESeoPageType.FILTER) {
            const categoriesResponse = await productApi.getCategories({
              parsed: 'false'
            })
            const categories = categoriesResponse.data as any[]

            const allSubCategories: ISubCategory[] = []
            for (const category of categories) {
              try {
                const subCategoriesResponse =
                  await productApi.getSubCategoriesByCategory(category._id)
                allSubCategories.push(...subCategoriesResponse.data)
              } catch {}
            }
            setSubCategories(allSubCategories)

            const selectedSubCategory = allSubCategories.find(
              (sub) => sub._id === pageData.subCategory_id
            )
            if (selectedSubCategory?.slug) {
              setCategoryPath(
                `horsesmart.store/category/${selectedSubCategory.slug}/`
              )
            }
          }
        } else {
          const categoriesResponse = await productApi.getCategories({
            parsed: 'false'
          })
          const categories = categoriesResponse.data as any[]

          const allSubCategories: ISubCategory[] = []
          for (const category of categories) {
            try {
              const subCategoriesResponse =
                await productApi.getSubCategoriesByCategory(category._id)
              allSubCategories.push(...subCategoriesResponse.data)
            } catch {}
          }
          setSubCategories(allSubCategories)
        }
      } catch (error) {
        setLoadingError(
          isEditMode
            ? 'Не удалось загрузить данные страницы'
            : 'Не удалось загрузить список подкатегорий'
        )
        showServerError(error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [id, isEditMode, form])

  const onFinish = async (values: ISeoPageFormData) => {
    try {
      setIsSubmitting(true)

      if (!values.name?.trim()) {
        message.error('Заполните текст селектора')
        return
      }

      if (!values.url?.trim()) {
        message.error('Заполните URL')
        return
      }

      if (isEditMode && id) {
        const updateData: IUpdateSeoPageRequest = {}

        if (values.name.trim() !== currentPage?.name) {
          updateData.name = values.name.trim()
        }

        if (values.subCategoryId !== currentPage?.subCategory_id) {
          updateData.subCategory_id = values.subCategoryId
        }

        const newUrl = `${categoryPath}${values.url.trim()}`
        if (newUrl !== currentPage?.url) {
          updateData.url = newUrl
        }

        const normalizeFilters = (filters: any[]) =>
          filters
            .map((filter) => ({
              name: filter.name,
              values: [...(filter.values || filter.selectedValues || [])].sort()
            }))
            .sort((a, b) => a.name.localeCompare(b.name))

        const currentFiltersNormalized = normalizeFilters(
          currentPage?.filters || []
        )
        const newFiltersNormalized = normalizeFilters(
          selectedFilters.map((filter) => ({
            name: filter.name,
            values: filter.selectedValues
          }))
        )

        const currentFiltersStr = JSON.stringify(currentFiltersNormalized)
        const newFiltersStr = JSON.stringify(newFiltersNormalized)

        if (currentFiltersStr !== newFiltersStr) {
          updateData.filters = selectedFilters.map((filter) => ({
            name: filter.name,
            values: [...filter.selectedValues]
          }))
        }

        const seoChanged =
          values.seoInfo?.title?.trim() !== currentPage?.seoInfo?.title ||
          values.seoInfo?.desc?.trim() !== currentPage?.seoInfo?.desc ||
          values.seoInfo?.h1?.trim() !== currentPage?.seoInfo?.h1 ||
          values.seoInfo?.seoText?.trim() !== currentPage?.seoInfo?.seoText

        if (seoChanged) {
          updateData.seoInfo = {
            title: values.seoInfo?.title?.trim() || undefined,
            desc: values.seoInfo?.desc?.trim() || undefined,
            h1: values.seoInfo?.h1?.trim() || undefined,
            seoText: values.seoInfo?.seoText?.trim() || undefined
          }
        }

        if (Object.keys(updateData).length > 0) {
          const response = await seoPageApi.updateSeoPage(id, updateData)
          setCurrentPage(response.data)

          if (updateData.subCategory_id) {
            setSelectedSubCategoryId(updateData.subCategory_id)
            setSelectedFilters([])

            const selectedSubCategory = subCategories.find(
              (sub) => sub._id === updateData.subCategory_id
            )
            if (selectedSubCategory?.slug) {
              const newCategoryPath = `horsesmart.store/category/${selectedSubCategory.slug}/`
              setCategoryPath(newCategoryPath)

              const currentUrl = form.getFieldValue('url') || ''

              if (
                currentUrl &&
                categoryPath &&
                currentUrl.includes(categoryPath)
              ) {
                const urlWithoutCategory = currentUrl.replace(categoryPath, '')
                form.setFieldValue('url', urlWithoutCategory)
              } else {
                form.setFieldValue('url', currentUrl)
              }
            }
          }

          message.success('SEO страница успешно обновлена')
        } else {
          message.info('Нет изменений для сохранения')
        }
      } else {
        if (!values.subCategoryId) {
          message.error('Выберите подкатегорию')
          return
        }

        const requestData: ICreateSeoPageRequest = {
          name: values.name.trim(),
          subCategory_id: values.subCategoryId,
          url: `${categoryPath}${values.url.trim()}`,
          filters: selectedFilters.map((filter) => ({
            name: filter.name,
            values: filter.selectedValues
          })),
          seoInfo: {
            title: values.seoInfo?.title?.trim() || undefined,
            desc: values.seoInfo?.desc?.trim() || undefined,
            h1: values.seoInfo?.h1?.trim() || undefined,
            seoText: values.seoInfo?.seoText?.trim() || undefined
          }
        }

        await seoPageApi.createSeoPage(requestData)
        message.success('SEO страница успешно создана')
      }
    } catch (error) {
      showServerError(error)
      message.error(
        isEditMode
          ? 'Не удалось обновить SEO страницу'
          : 'Не удалось создать SEO страницу'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const onCancel = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Несохраненные изменения',
        icon: <ExclamationCircleOutlined />,
        content:
          'У вас есть несохраненные изменения. Вы уверены, что хотите покинуть страницу?',
        okText: 'Да, покинуть',
        cancelText: 'Остаться',
        onOk: () => navigate(SEO_PAGES_LIST_PATH)
      })
    } else {
      navigate(superAdminRoutes.SEO_PAGES)
    }
  }

  const handleSelectorTextBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const selectorText = e.target.value?.trim()
    const currentUrl = form.getFieldValue('url')
    if (selectorText && !currentUrl && !isUrlManuallyEdited) {
      const urlFriendlyText = convertToUrlFriendly(selectorText)
      form.setFieldValue('url', urlFriendlyText)
      // Принудительно запускаем валидацию поля URL после автозаполнения
      form.validateFields(['url'])
    }
  }

  const handleUrlChange = () => {
    setIsUrlManuallyEdited(true)
  }

  const handleDelete = () => {
    if (!isEditMode || !id) return

    Modal.confirm({
      title: 'Подтверждение удаления',
      icon: <ExclamationCircleOutlined />,
      content:
        'Вы уверены, что хотите удалить эту SEO страницу? Это действие нельзя отменить.',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setIsDeleting(true)
          await seoPageApi.deleteSeoPage(id)
          message.success('SEO страница успешно удалена')
          navigate(SEO_PAGES_LIST_PATH)
        } catch (error) {
          showServerError(error)
          message.error('Не удалось удалить SEO страницу')
        } finally {
          setIsDeleting(false)
        }
      }
    })
  }

  const subCategoryOptions = React.useMemo(
    () =>
      subCategories.map((subCategory) => ({
        value: subCategory._id,
        label: subCategory.name,
        slug: subCategory.slug
      })),
    [subCategories]
  )

  const retryLoadSubCategories = () => {
    const getSubCategories = async () => {
      try {
        setIsLoading(true)
        setLoadingError(null)
        const categoriesResponse = await productApi.getCategories({
          parsed: 'false'
        })
        const categories = categoriesResponse.data as any[]

        const allSubCategories: ISubCategory[] = []
        for (const category of categories) {
          try {
            const subCategoriesResponse =
              await productApi.getSubCategoriesByCategory(category._id)
            allSubCategories.push(...subCategoriesResponse.data)
          } catch {
            // Continue if some category fails
          }
        }
        setSubCategories(allSubCategories)
      } catch (error) {
        setLoadingError('Не удалось загрузить список подкатегорий')
        showServerError(error)
      } finally {
        setIsLoading(false)
      }
    }

    getSubCategories()
  }

  const renderHeader = () => (
    <>
      <MainTitle
        text={
          isEditMode
            ? currentPage?.name || 'Редактирование SEO страницы'
            : 'Создание страницы фильтров'
        }
        isGoBack
        onGoBack={() => navigate(SEO_PAGES_LIST_PATH)}
      />
      {isEditMode && currentPage && (
        <p
          style={{
            marginTop: '-10px',
            marginBottom: '20px',
            color: '#000',
            fontSize: '18px'
          }}
        >
          Фильтры
        </p>
      )}
    </>
  )

  if (isLoading && isEditMode) {
    return (
      <div className={styles.container}>
        <MainTitle
          text='Загрузка...'
          isGoBack
          onGoBack={() => navigate(SEO_PAGES_LIST_PATH)}
        />
        <div
          style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}
        >
          <Spin size='large' />
        </div>
      </div>
    )
  }

  if (loadingError && !isLoading && subCategories.length === 0) {
    return (
      <div className={styles.container}>
        {renderHeader()}
        <Result
          status='error'
          title='Ошибка загрузки данных'
          subTitle={loadingError}
          extra={[
            <Button
              key='retry'
              type='primary'
              icon={<ReloadOutlined />}
              onClick={retryLoadSubCategories}
            >
              Попробовать снова
            </Button>,
            <Button key='back' onClick={onCancel}>
              Назад к списку
            </Button>
          ]}
        />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {renderHeader()}

      {loadingError && subCategories.length > 0 && (
        <Alert
          message='Предупреждение'
          description={loadingError}
          type='warning'
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setLoadingError(null)}
        />
      )}

      <Form
        form={form}
        name='seoPageForm'
        size='large'
        onFinish={onFinish}
        layout='vertical'
        className={styles.form}
        onValuesChange={() => setHasUnsavedChanges(true)}
      >
        <Form.Item
          name='subCategoryId'
          label='Подкатегория'
          rules={[
            {
              required: !isEditMode,
              message: 'Выберите подкатегорию'
            }
          ]}
          hasFeedback
        >
          <Select
            placeholder={
              isLoading ? 'Загрузка подкатегорий...' : 'Выберите подкатегорию'
            }
            loading={isLoading}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={subCategoryOptions}
            onChange={(value) => {
              setSelectedSubCategoryId(value)
              setSelectedFilters([])

              const selectedSubCategory = subCategories.find(
                (sub) => sub._id === value
              )
              if (selectedSubCategory?.slug) {
                const oldCategoryPath = categoryPath
                const newCategoryPath = `horsesmart.store/category/${selectedSubCategory.slug}/`
                setCategoryPath(newCategoryPath)

                // Получаем текущее значение URL формы
                const currentUrl = form.getFieldValue('url') || ''

                // Если URL содержит старую categoryPath, заменяем её на новую
                if (
                  currentUrl &&
                  oldCategoryPath &&
                  currentUrl.includes(oldCategoryPath)
                ) {
                  const urlWithoutCategory = currentUrl.replace(
                    oldCategoryPath,
                    ''
                  )
                  form.setFieldValue('url', urlWithoutCategory)
                } else {
                  // Если URL не содержит categoryPath или это новый URL, оставляем как есть
                  form.setFieldValue('url', currentUrl)
                }

                // Сбрасываем валидацию полей
                form.setFields([
                  { name: 'url', errors: [] },
                  { name: 'name', errors: [] }
                ])
              }
            }}
            notFoundContent={
              isLoading ? 'Загрузка...' : 'Подкатегории не найдены'
            }
          />
        </Form.Item>

        <Form.Item
          name='name'
          label='Текст селектора'
          rules={[
            {
              required: true,
              message: 'Заполните текст селектора'
            },
            {
              whitespace: true,
              message: 'Заполните текст селектора'
            },
            { validator: validateSelectorText }
          ]}
          hasFeedback
        >
          <Input
            placeholder={
              selectedSubCategoryId
                ? 'Введите текст селектора (русский или английский)'
                : 'Сначала выберите подкатегорию'
            }
            onBlur={handleSelectorTextBlur}
            disabled={!selectedSubCategoryId}
          />
        </Form.Item>

        <Form.Item
          name='url'
          label='URL-страница'
          rules={[
            {
              required: true,
              message: 'Заполните URL'
            },
            {
              whitespace: true,
              message: 'Заполните URL'
            },
            {
              validator: validateUrl
            }
          ]}
          hasFeedback
        >
          <Input
            addonBefore={categoryPath}
            placeholder={
              selectedSubCategoryId
                ? 'заполнится автоматически из текста селектора (транслит)'
                : 'сначала выберите подкатегорию'
            }
            onChange={handleUrlChange}
          />
        </Form.Item>

        <div className={styles.filtersSection}>
          <FilterSelector
            subCategoryId={selectedSubCategoryId}
            selectedFilters={selectedFilters}
            onChange={setSelectedFilters}
          />
        </div>

        <div className={styles.seoSection}>
          <SeoFields collapsed={true} defaultOpen={true} />
        </div>

        <div className={styles.actions}>
          <Button
            type='primary'
            htmlType='submit'
            loading={isSubmitting}
            size='large'
          >
            Сохранить
          </Button>
          {isEditMode && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={isDeleting}
              size='large'
            >
              Удалить
            </Button>
          )}
          <Button htmlType='button' onClick={onCancel} size='large'>
            Отмена
          </Button>
        </div>
      </Form>
    </div>
  )
}
